//////////////////////////////////////////////////////////////////////////////
// GLG RealTimeChartSimple example demonstrates how to use a GLG RealTimeChart 
// widget chart2.g.
//
// GetData() method supplies simulated data for animation. An application 
// should provide a custom implementation of this method to supply real-time
// application data to the chart.
//////////////////////////////////////////////////////////////////////////////

/* eslint eqeqeq: 0, no-unused-vars: 0 */
/* eslint @typescript-eslint/no-unused-vars: 0 */

import { GlgToolkit } from '../GlgToolkitDemo.mod.js'

// Enable general debugging/diagnostics information.
const DEBUG = false;

/* Debugging: set the variable to true to throw an exception on a GLG error
   instead of just displaying an error message on the console.
*/
const DEBUG_GLG_ERRORS = true;

/* Is set to true, simulated demo data will be used for animation.
   Set to false to enable live application data generated by
   GetAppData().
*/
const RANDOM_DATA = true;

const DRAWING_NAME = "chart2.g";   // GLG drawing filename.

const UPDATE_INTERVAL = 100;       // Update interval in msec.

const TIME_SPAN = 60.;             // Time Span in sec.

/* If set to true, the number of plots is defined in the drawing,
   otherwise it is defined by the NUM_PLOTS constant below.
*/
const USE_NUM_PLOTS_FROM_DRAWING = false;

// Defines number of lines in a chart when USE_NUM_PLOTS_FROM_DRAWING=false.
const NUM_PLOTS = 3;

// Provides data for YAxis levels (thresholds).
const Levels = [ 
   { level: 1.0, color: { r:0.0, g:0.0, b:1.0 } },
   { level: 9.0, color: { r:1.0, g:0.0, b:0.0 } }
];

// High/Low range of the simulated demo data.
const DemoDataLow = 0;
const DemoDataHigh = 10;

// Global handle to the GLG Toolkit library.
let GLG = new GlgToolkit();

//////////////////////////////////////////////////////////////////////////////
// Creates an instance of the real time chart.
// Parameters:
//   glg_div_name  - name of parent div the drawing will be displayed in,
//                   will be passed by the caller.
//   is_mobile     - true if deployed on mobile devices.
//   is_standalone - true if deployed in html, false if deployed in react or
//                   angular.
//   glg_path      - path to the directory where GLG drawings are located.
//   title         - an optional title string
//////////////////////////////////////////////////////////////////////////////
export function GlgRTChartSimple( glg_div_name, glg_path,
                                  is_standalone, is_mobile, title )
{
   Debug( "New script for " + glg_div_name + ": "  +
          Object.getPrototypeOf( this ).constructor.name +
          " glg_path: " + glg_path );
   
   if( DEBUG_GLG_ERRORS )   
     GLG.ThrowExceptionOnError( true, true, true );
   else
     GLG.ThrowExceptionOnError( false, false, false );

   this.GLG_div_name = glg_div_name;
   this.IsMobile = is_mobile;
   this.Standalone = is_standalone;    

   // Use path to the drawings directory is supplied.
   this.GlgPath = glg_path;
           
   // Use title if supplied. 
   this.Title = ( title ? title : "RealTime Chart" );

   this.DrawingName = DRAWING_NAME;
   
   /* Initial Low and High range. The chart may use auto-scaling to adjust
      low and high ranges to fit incoming data. 
      In the demo mode, set to the demo data range.
   */
   this.Low = DemoDataLow; 
   this.High = DemoDataHigh;

   // Time axis span in sec.
   this.TimeSpan = TIME_SPAN;

   /* By default, use NUM_PLOTS constant to define the number of chart plots. 
      If USE_NUM_PLOTS_FROM_DRAWING=true, the number of plots will be set
      to the number of plots in the loaded drawing in InitBeforeH().
   */   
   this.NumPlots = NUM_PLOTS;

   // Top level viewport of the loaded drawing.   
   this.Viewport = null;

   // Dynamically created array that stores plot objects in a chart.
   this.Plots = null;

   /* Object used to store and pass information about one data sample.
      Set has_time_stamp=true to supply time_stamp explicitly via the object's
      time_stamp property. Otherwise, the chart will automatically display a
      time stamp using current time.
   */
   this.DataPoint = {
      value : 0,
      value_valid : true,          
      time_stamp : 0,
      has_time_stamp : false
   };

   this.UpdateTimer = null; 
 
   this.Counter = 0;    // Counter used for simulated data.

   /* Coefficient for canvas resolution. It will be adjusted in 
      SetCanvasResolution() for mobile devices with HiDPI displays as well as 
      on browser zoom.
   */
   this.CoordScale = 1;
}

////////////////////////////////////////////////////////////////////////////// 
// Script entry point: Starts the chart by loading its drawing.
////////////////////////////////////////////////////////////////////////////// 
GlgRTChartSimple.prototype.Start = function()
{
   Debug( "Starting: " + this.GLG_div_name );

   this.Active = true;
   
   // Set initial size of the drawing.
   this.SetDrawingSize( false );

   /* Load misc. assets such as GLG scrollbars. When assets are loaded, 
      LoadDrawing callback is invoked that loads a GLG drawing defined by
      DrawingName variable.
   */
   this.LoadAssets( ()=>this.LoadDrawing(), null );
}

////////////////////////////////////////////////////////////////////////////// 
// Script entry point: Performs cleanup.
////////////////////////////////////////////////////////////////////////////// 
GlgRTChartSimple.prototype.Cleanup = function()
{
   Debug( "Cleanup for: " + this.GLG_div_name );
   
   this.Active = false;    // Ignore any pending updates and callbacks.

   if( this.Viewport )
     this.Viewport.ResetHierarchy();   // Undisplay GLG drawing.

   this.StopUpdateTimer();

   if( this.ResizeListener )
     window.removeEventListener( "resize", this.ResizeListener );
}
   
////////////////////////////////////////////////////////////////////////////// 
// Load a GLG drawing from a file.
////////////////////////////////////////////////////////////////////////////// 
GlgRTChartSimple.prototype.LoadDrawing = function()
{
   Debug( "LoadDrawing for: " + this.GLG_div_name );
   
   if( !this.Active )
     return;

   /* Load a drawing from the specified drawing file. 
      The LoadCB callback will be invoked when the drawing has been loaded.

      Using "bind( this )" as a shorter way to provide "this" compared with 
      using lambda: with bind, we do not need to specify parameter list that
      we would need to provide for lambda.
   */
   GLG.LoadWidgetFromURL( this.GetFullName( this.DrawingName ), null,
                          this.LoadCB.bind( this ), /*user data*/ null,
                          /*abort test function*/ ()=>!this.Active );
}

////////////////////////////////////////////////////////////////////////////// 
GlgRTChartSimple.prototype.GetFullName = function( drawing_name )
{
   if( this.GlgPath == null )
     return drawing_name;

   return this.GlgPath + "/" + drawing_name;
}

//////////////////////////////////////////////////////////////////////////////
GlgRTChartSimple.prototype.LoadCB =
  function( /*GlgObject*/ drawing, /*Object*/ user_data, /*String*/ path )
{
   Debug( "LoadCB for: " + this.GLG_div_name );

   if( !this.Active )
     return;

   if( drawing == null )
   {
      AppAlert( "Can't load drawing, check console message for details." );
      return;
   }
   
   if( !document.getElementById( this.GLG_div_name ) )
   {
      Debug( "Can't find " + this.GLG_div_name +
             " div: it may have been removed from the document." );
      return;
   }
    
   // Disable spinning loader.   
   RemoveElement( this.GLG_div_name, "loader_container" );
    
   // Define the element in the HTML page to display the drawing.
   drawing.SetParentElement( this.GLG_div_name );
    
   // Disable viewport border to use the border of the glg_area.
   if( this.Standalone )
     drawing.SetDResource( "LineWidth", 0 );
    
   this.StartGlgChart( drawing );
}

//////////////////////////////////////////////////////////////////////////////
GlgRTChartSimple.prototype.StartGlgChart = function( /*GlgObject*/ drawing )
{
   this.Viewport = drawing;
    
   // Initialization before hierarchy setup.
   this.InitBeforeH();

   // Setup object hierarchy of the drawing.
   this.Viewport.SetupHierarchy();

   // Initialization after hierarchy setup.
   this.InitAfterH();

   // Start dynamic updates.
   this.StartUpdateTimer();

   // Display the drawing in a web page.
   this.Viewport.Update();
}

//////////////////////////////////////////////////////////////////////////////
// Initialization before hierarchy setup.
//////////////////////////////////////////////////////////////////////////////
GlgRTChartSimple.prototype.InitBeforeH = function()
{
   // Add Input callback to hadle user interaction.
   this.Viewport.AddListener( GLG.GlgCallbackType.INPUT_CB,
                              this.InputCallback.bind( this ) );

   // Retrieve the number of plots defined in the drawing.
   this.NumPlotsInDrawing = this.Viewport.GetDResource( "Chart/NumPlots" );

   if( USE_NUM_PLOTS_FROM_DRAWING )
     this.NumPlots = this.NumPlotsInDrawing;
   
   // Set number of plots as needed.
   this.Viewport.SetDResource( "Chart/NumPlots", this.NumPlots );
    
   // Set Chart title.
   if( this.Title )
     this.Viewport.SetSResource( "Title/String", this.Title );

   // Set Time Span for the X axis.
   this.Viewport.SetDResource( "Chart/XAxis/Span", this.TimeSpan );
    
   /* Uncomment the line below to override XAxis label TimeFormat 
      defined in the drawing. "%T" displays time without a date.
   */
   //this.Viewport.SetSResource( "Chart/XAxis/TimeFormat", "%T" );

   /* Set tick intervals for the Time axis.
      Use positive values for absolute time interval, for example
      set major_interval = 10 for a major tick every 10 sec.
   */
   const major_interval = -6;     // 6 major intervals
   const minor_interval = -5;     // 5 minor intervals
   this.Viewport.SetDResource( "Chart/XAxis/MajorInterval", major_interval );
   this.Viewport.SetDResource( "Chart/XAxis/MinorInterval", minor_interval );
    
   /* Set data value range. Since the graph has one Y axis and common data range
      for the plots, Low/High data range is set on the YAxis level.
   */
   this.Viewport.SetDResource( "Chart/YAxis/Low", this.Low );
   this.Viewport.SetDResource( "Chart/YAxis/High", this.High );

   /* Set number of level thresholds to the number of level data objects in
      the Levels array. Comment out this line to use all levels defined in the
      loaded drawing.
   */
   this.Viewport.SetDResource( "Chart/NumLevels", Levels.length );
}

//////////////////////////////////////////////////////////////////////////////
// Initialization after hierarchy setup.
//////////////////////////////////////////////////////////////////////////////
GlgRTChartSimple.prototype.InitAfterH = function()
{
   /* Allocate Plots array and store object IDs for each plot. 
      Assign line color and/or line annotation as needed.
   */
   this.Plots = new Array( this.NumPlots );
   for( let i=0; i<this.NumPlots; ++i )
   {
      this.Plots[i] = this.Viewport.GetNamedPlot( "Chart", "Plot#" + i ); 
   }
    
   /* For the existing plots, use color and line annotation setting from the 
      drawing. If NumPlots > NumPlotsInDrawing, initialize new plots using
      random colors and strings for demo purposes.
   */
   for( let i=this.NumPlotsInDrawing; i < this.NumPlots; ++i )
   {
      // Using a random color for a demo.
      this.Plots[i].SetGResource( "EdgeColor", 
                                  Math.random(), Math.random(), Math.random() );
      this.Plots[i].SetSResource( "Annotation", "Var" + i );
   }

   // Set YAxis levels.
   let num_levels = this.Viewport.GetDResource( "Chart/NumLevels" );
   if( num_levels <= Levels.length )
   {
      for( let i=0; i<num_levels; ++i )
      {
         let res_name = "Chart/Levels/Level#" + i + "/";

         // Asssign level value.
         this.Viewport.SetDResource( res_name + "Level", Levels[i].level );

         // Assign level color.
         this.Viewport.SetGResource( res_name + "EdgeColor", 
                                     Levels[i].color.r,
                                     Levels[i].color.g,
                                     Levels[i].color.b );  
      }
   }
}

//////////////////////////////////////////////////////////////////////////
GlgRTChartSimple.prototype.StartUpdateTimer = function()
{
   this.UpdateTimer = setTimeout( ()=>this.UpdateChart(), UPDATE_INTERVAL );
}

//////////////////////////////////////////////////////////////////////////
GlgRTChartSimple.prototype.StopUpdateTimer = function()
{
   if( this.UpdateTimer != null )
   {
      clearTimeout( this.UpdateTimer );
      this.UpdateTimer = null;
   }
}

//////////////////////////////////////////////////////////////////////////////
// Animation: obtain a new data value for each plot and push it into graphics.
//////////////////////////////////////////////////////////////////////////////
GlgRTChartSimple.prototype.UpdateChart = function()
{
   if( !this.Active || this.UpdateTimer == null )
     return;   // Updates were stopped on cleanup : ignore data.
   
   // Supply real-time data to update plot lines.
   for( let i=0; i<this.NumPlots; ++i )
   {
      this.GetData( i, this.DataPoint );
      this.PushPlotPoint( i, this.DataPoint );
   }

   // Refresh display.
   this.Viewport.Update();

   // Restart the update timer.
   this.StartUpdateTimer();
}

//////////////////////////////////////////////////////////////////////////////
GlgRTChartSimple.prototype.GetData = function( plot_index, data_point )
{
   if( RANDOM_DATA )
     // Use simulated demo data.
     this.GetDemoData( plot_index, data_point );
   else
     // Use application real-time data.
     this.GetAppData( plot_index, data_point );
}

//////////////////////////////////////////////////////////////////////////////
// Use simulated demo data for animation.
//////////////////////////////////////////////////////////////////////////////
GlgRTChartSimple.prototype.GetDemoData = function( plot_index, data_point )
{
   data_point.value = this.GetDemoValue( plot_index );
   
   /* If data_point.has_time_stamp=true, supply time stamp explicitly
      using current time for demo. 
      If data_point.has_time_stamp=false, the chart will automatically 
      display current time as an XAxis label. 
   */
   if( data_point.has_time_stamp )
     data_point.time_stamp = GetCurrTime();
   
   /* For demo purposes, show all data points as valid.
      The application may set value_valid=false for a data sample with an 
      invalid value to show a hole in the chart for that data sample.
   */   
   data_point.value_valid = true;
}

//////////////////////////////////////////////////////////////////////////////
// Place custom code here to query real-time data for the given plot index.
//////////////////////////////////////////////////////////////////////////////
GlgRTChartSimple.prototype.GetAppData = function( plot_index, data_point )
{
   /*
     data_point.value = 
     data_point.time_stamp =
     data_point.has_time_stamp =
     data_point.value_valid = 
   */   
}

//////////////////////////////////////////////////////////////////////////////
// Push new plot data sample for a given plot index into graphics.
//////////////////////////////////////////////////////////////////////////////
GlgRTChartSimple.prototype.PushPlotPoint = function( plot_index, data_point )
{
   let plot = this.Plots[ plot_index ];
    
   // Supply plot value for the chart via ValueEntryPoint.
   plot.SetDResource( "ValueEntryPoint", data_point.value );
    
   if( data_point.has_time_stamp )
   {
      /* Supply an optional time stamp. If not supplied, the chart will 
         automatically generate a time stamp using current time. 
      */
      plot.SetDResource( "TimeEntryPoint", data_point.time_stamp );
   }
    
   if( !data_point.value_valid )
   {	   
      /* If the data point is not valid, set ValidEntryPoint resource to 
         display a hole for an invalid data point. If the point is valid,
         it is automatically set to 1.0 by the chart.
      */
      plot.SetDResource( "ValidEntryPoint", 0.0 );
   }
}

//////////////////////////////////////////////////////////////////////////////
// Handle user interaction as needed.
//////////////////////////////////////////////////////////////////////////////
GlgRTChartSimple.prototype.InputCallback = function( viewport, message_obj )
{
   if( !this.Active )
     return;
    
   let origin = message_obj.GetSResource( "Origin" );
   let format = message_obj.GetSResource( "Format" );
   let action = message_obj.GetSResource( "Action" );
    
   if( format == "Button" )
   {	 
      if( action !="Activate" &&         /* Not a push button */
          action != "ValueChanged" )     /* Not a toggle button */
        return;
        
      this.Viewport.Update();  // format = "Button"
   }
}

//////////////////////////////////////////////////////////////////////////////
// Changes drawing size while maintaining width/height aspect ratio.
//////////////////////////////////////////////////////////////////////////////
GlgRTChartSimple.prototype.SetDrawingSize = function( next_size )
{
   const ASPECT_RATIO = 700 / 540;
    
   const MIN_WIDTH = 500;
   const MAX_WIDTH = 900;
   const SCROLLBAR_WIDTH = 15;

   let span = document.body.clientWidth - SCROLLBAR_WIDTH;    

   if( this.SizeIndex == undefined )   // First time: initialize.
   {
      this.SizeIndex = 0;

      if( this.IsMobile )
      {
         /* Mobile devices use fixed device-width: disable Change Drawing Size 
            button. If it's not standalone, it is handled outside of this 
            script.
         */
         if( this.Standalone )
           RemoveElement( null, this.GLG_div_name + "_change_size" );
      }
      else   /* Desktop */
      {      
         const small_sizes  = [ 1, 1.5,  2.,   2.5 ];
         const medium_sizes = [ 1, 0.75, 1.25, 1.5 ];
         const large_sizes  = [ 1, 0.6,  1.25, 1.5 ];
         
         if( span < 600 )
           this.SetDrawingSize.size_array = small_sizes;
         else if( span < 800 )
           this.SetDrawingSize.size_array = medium_sizes;
         else
           this.SetDrawingSize.size_array = large_sizes;
         
         this.SetDrawingSize.num_sizes = this.SetDrawingSize.size_array.length;
         
         /* Handle browser zooming. */
         this.ResizeListener = ()=>this.SetDrawingSize( false );
         window.addEventListener( "resize", this.ResizeListener );
      }
   }
   else if( next_size )
   {
      ++this.SizeIndex;
      this.SizeIndex %= this.SetDrawingSize.num_sizes;
   }
    
   let drawing_area = document.getElementById( this.GLG_div_name );
   if( this.IsMobile )
   {
      /* Mobile devices use constant device-width, adjust only the height 
         of the drawing to keep the aspect ratio.
      */
      drawing_area.style.height =
        "" + Math.trunc( drawing_area.clientWidth / ASPECT_RATIO ) + "px";
   }
   else   /* Desktop */
   {
      let start_width;
      if( span < MIN_WIDTH )
        start_width = MIN_WIDTH;
      else if( span > MAX_WIDTH )
        start_width = MAX_WIDTH;
      else
        start_width = span;

      let size_coeff = this.SetDrawingSize.size_array[ this.SizeIndex ];
      let width = Math.trunc( Math.max( start_width * size_coeff, MIN_WIDTH ) );
      drawing_area.style.width = "" + width + "px";
      drawing_area.style.height = 
        "" + Math.trunc( width / ASPECT_RATIO ) + "px";
   }

   // Adjust canvas resolution for mobile devices and browser zoom state.
   if( !next_size )
     this.SetCanvasResolution();
}

//////////////////////////////////////////////////////////////////////////////
// Increases canvas resolution for mobile devices with HiDPI displays and for
// browser zooming. Sets CoordScale global variable.
//////////////////////////////////////////////////////////////////////////////
GlgRTChartSimple.prototype.SetCanvasResolution = function()
{
   let TextScale, PixelOffsetScale, ScreenCoordScale, NativeWidgetTextScale;

   if( this.IsMobile )
   {
      /* CoordScale parameter defines canvas coordinate scaling.
         Values greater than 1 increase canvas resolution and result in 
         sharper rendering. On mobile devices with devicePixelRatio > 1,
         the value of devicePixelRatio may be used for very crisp rendering
         with very thin lines. For pixel ration greater than 2, limit 
         CoordScale to 2 to draw thicker lines.

         CanvasScale > 1 makes text smaller. The TextScale parameter defines
         the text scaling factor used to increase text size.

         The ScreenCoordScale parameter specifies a scaling factor for fixed
         scale viewports that use screen coordinates. If the size of the fixed
         scale viewport (such as a height of a toolbar) is controlled by a pixel
         offset transformation, ScreenCoordScale may be set to the same value as
         PixelOffsetScale to scale the content of the viewport proportionally
         to its height increase.

         The NativeWidgetTextScale parameter defines the scaling factor that is
         used to scale down text in native widgets (such as native buttons, 
         toggles, etc.) to match the scale of the drawing.
      */
      if( window.devicePixelRatio > 2. )
        this.CoordScale = 2.;
      else
        this.CoordScale = window.devicePixelRatio;

      TextScale = 1.5;
      PixelOffsetScale = TextScale;
      ScreenCoordScale = PixelOffsetScale;
      NativeWidgetTextScale = 0.6;
   }
   else   // Desktop
   {
      /* Change canvas resolution to match browser zoom state. */
      this.CoordScale = window.devicePixelRatio;      
      TextScale = window.devicePixelRatio;
      PixelOffsetScale = window.devicePixelRatio;
      ScreenCoordScale = window.devicePixelRatio;
      NativeWidgetTextScale = 1.;    // Don't change native text size.
   }

   GLG.SetCanvasScale( this.CoordScale, TextScale, NativeWidgetTextScale,
                       PixelOffsetScale, ScreenCoordScale );
}

//////////////////////////////////////////////////////////////////////////
function RemoveElement( parent_id, child_id )
{
   let element;
   if( parent_id == null )
     // No parent was provided: remove from document.
     element = document.getElementById( child_id );
   else
   {
      // Parent ID was provided: remove from parent.
      let parent = document.getElementById( parent_id );
      if( parent == null )
        return;
      element = parent.querySelector( "#" + child_id );
   }
   
   if( element != null )
     element.parentNode.removeChild( element );
}

//////////////////////////////////////////////////////////////////////////////
// Loads any assets required by the application and invokes the specified
// callback when done.
// Alternatively, the application drawing can be loaded as an asset here
// as well, so that it starts loading without waiting for the other assets 
// to finish loading.
//////////////////////////////////////////////////////////////////////////////
GlgRTChartSimple.prototype.LoadAssets = function( callback, user_data )
{
   Debug( "LoadAssets for: " + this.GLG_div_name );
   
   if( !this.Active )
     return;

   /* Define an internal variable to keep the number of loaded assets. */
   this.NumLoadedAssets = 0;

   /* HTML5 doesn't provide a scrollbar input element (only a range input 
      html element is available). This application needs to load GLG scrollbars
      used for integrated chart scrolling. For each loaded scrollbar, the 
      AssetLoaded callback is invoked with the supplied data array parameter.

      Using "bind( this )" as a shorter way to provide "this" compared with 
      using lambda: with bind, we do not need to specify parameter list that
      we would need to provide for lambda.
   */
   GLG.LoadWidgetFromURL( this.GetFullName( "scrollbar_h.g" ), null,
                          this.AssetLoaded.bind( this ),
                          { name: "scrollbar_h", callback: callback,
                            user_data: user_data },
                          /*abort test function*/ ()=>!this.Active );
   GLG.LoadWidgetFromURL( this.GetFullName( "scrollbar_v.g" ), null,
                          this.AssetLoaded.bind( this ),
                          { name: "scrollbar_v", callback: callback,
                            user_data: user_data },
                          /*abort test function*/ ()=>!this.Active );
}

//////////////////////////////////////////////////////////////////////////////
GlgRTChartSimple.prototype.AssetLoaded = function( glg_object, data, path )
{
   if( !this.Active )
     return;
   
   if( data.name == "scrollbar_h" )
   {
      if( glg_object != null )
        glg_object.SetResourceObject( "$config/GlgHScrollbar", glg_object );
   }
   else if( data.name == "scrollbar_v" )
   {
      if( glg_object != null )
        glg_object.SetResourceObject( "$config/GlgVScrollbar", glg_object );
   }
   else
     AppError( "Unexpected asset name" );

   ++this.NumLoadedAssets;
    
   // Invoke the callback after all assets have been loaded.
   if( this.NumLoadedAssets == 2 )
     data.callback( data.user_data );
}

//////////////////////////////////////////////////////////////////////////
function GetCurrTime()
{
   return Date.now() / 1000;    // seconds
}   

//////////////////////////////////////////////////////////////////////////////
// Returns simulated data value.
//////////////////////////////////////////////////////////////////////////////
GlgRTChartSimple.prototype.GetDemoValue = function( plot_index )
{
   let half_amplitude = ( DemoDataHigh - DemoDataLow ) / 2.0;
   let center = DemoDataLow + half_amplitude;
   let period = 100.0 * ( 1.0 + plot_index * 2.0 );
   let alpha = 2.0 * Math.PI * this.Counter / period;
    
   let value = center + 
     half_amplitude * Math.sin( alpha ) * Math.sin( alpha / 30.0 );
    
   ++this.Counter;
   return value;
}

//////////////////////////////////////////////////////////////////////////////
function Debug( message )
{
   if( DEBUG )
     console.log( message );
}

//////////////////////////////////////////////////////////////////////////////
function AppError( message )
{
   console.error( message );
}

//////////////////////////////////////////////////////////////////////////////
function AppAlert( message )
{
   window.alert( message );
}
